import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import * as api from "../../distflowAPI/plannerApi";
import {
  dateKey,
  errorMessage,
  formatDate,
  repeatLabels,
} from "./helpers";

const Select = ({ children, ...props }) => (
  <TextField
    select
    SelectProps={{ native: true }}
    fullWidth
    size="small"
    InputLabelProps={{ shrink: true }}
    {...props}
  >
    {children}
  </TextField>
);
const DateInput = (props) => (
  <TextField
    type="date"
    size="small"
    fullWidth
    InputLabelProps={{ shrink: true }}
    {...props}
  />
);
function Repeat({ value, onChange, disabled }) {
  return (
    <Select
      label="Repeat"
      value={value}
      onChange={onChange}
      disabled={disabled}
    >
      {Object.entries(repeatLabels).map(([id, label]) => (
        <option key={id} value={id}>
          {label}
        </option>
      ))}
    </Select>
  );
}

export function TaskEditor({
  item,
  people,
  user,
  onClose,
  onSaved,
  onDeleted,
}) {
  const [form, setForm] = useState(() => ({
    title: "",
    description: "",
    due_date: "",
    recurrence: "none",
    reminder_time: "",
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
    visibility: "personal",
    assigned_to: user.id,
    checklist: [],
    ...item,
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canEdit = !item.id || item.can_edit;
  const set = (key) => (event) =>
    setForm((f) => ({ ...f, [key]: event.target.value }));
  useEffect(() => {
    let active = true;
    if (item.id)
      api
        .taskHistory(item.id)
        .then(({ data }) => {
          if (active) setHistory(data);
        })
        .catch(() => {
          if (active) setHistory("error");
        });
    return () => {
      active = false;
    };
  }, [item.id]);
  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const payload = {
      title: form.title.trim(),
      description: form.description,
      due_date: form.due_date || null,
      assigned_to: Number(form.assigned_to),
      visibility:
        Number(form.assigned_to) !== (item.created_by || user.id)
          ? "team"
          : form.visibility,
      recurrence: form.recurrence,
      reminder_time: form.reminder_time || null,
      timezone: form.timezone,
      checklist: form.checklist,
      ...(item.updated_at ? { version: item.updated_at } : {}),
    };
    try {
      const { data } = await (item.id
        ? api.updateTask(item.id, payload)
        : api.createTask(payload));
      onSaved(data);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    setBusy(true);
    setError("");
    try {
      await api.deleteTask(item.id);
      onDeleted(item.id);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  function checklist(index, changes) {
    setForm((f) => ({
      ...f,
      checklist: f.checklist.map((step, i) =>
        i === index ? { ...step, ...changes } : step,
      ),
    }));
  }
  const shared = Number(form.assigned_to) !== (item.created_by || user.id);
  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="task-editor-title"
    >
      <Box component="form" onSubmit={save}>
        <DialogTitle id="task-editor-title">
          {item.id ? "Task details" : "Add to checklist"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {!canEdit && (
              <Alert severity="info">
                The creator and assignee can edit this task.
              </Alert>
            )}
            {item.projected && (
              <Alert severity="info">
                This is a future repeat. Changes apply to the recurring task.
              </Alert>
            )}
            <TextField
              autoFocus
              label="Title"
              required
              inputProps={{ maxLength: 240 }}
              fullWidth
              value={form.title}
              onChange={set("title")}
              disabled={!canEdit || busy}
            />
            <TextField
              label="Notes"
              multiline
              minRows={2}
              value={form.description}
              onChange={set("description")}
              disabled={!canEdit || busy}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Select
                label="Assigned to"
                value={form.assigned_to}
                onChange={set("assigned_to")}
                disabled={!canEdit || busy}
              >
                {!people.some((p) => p.id === Number(form.assigned_to)) && (
                  <option value={form.assigned_to}>
                    {item.assigned_name || "Me"}
                  </option>
                )}
                {people.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                    {person.id === user.id ? " (me)" : ""}
                  </option>
                ))}
              </Select>
              <Select
                label="Visibility"
                value={shared ? "team" : form.visibility}
                onChange={set("visibility")}
                disabled={!canEdit || busy || shared}
              >
                <option value="personal">Only me</option>
                <option value="team">Team</option>
              </Select>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <DateInput
                label="Due date"
                value={form.due_date || ""}
                onChange={set("due_date")}
                required={
                  form.recurrence !== "none" || Boolean(form.reminder_time)
                }
                disabled={!canEdit || busy}
              />
              <Repeat
                value={form.recurrence}
                onChange={set("recurrence")}
                disabled={!canEdit || busy}
              />
            </Stack>
            {form.recurrence !== "none" && (
              <Typography variant="caption" color="text.secondary">
                Repeats from the due date. Completing it keeps the history and
                schedules the next future date.
              </Typography>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                type="time"
                label="Reminder time"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={form.reminder_time || ""}
                onChange={set("reminder_time")}
                disabled={!canEdit || busy}
              />
              <TextField
                label="Timezone"
                fullWidth
                size="small"
                value={form.timezone}
                onChange={set("timezone")}
                disabled={!canEdit || busy}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Optional reminders appear inside Distflow while you have it open.
            </Typography>
            <Divider />
            <Typography fontWeight={600}>Checklist</Typography>
            {form.checklist.map((step, index) => (
              <Stack
                key={index}
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <Checkbox
                  inputProps={{
                    "aria-label": step.text || `Complete step ${index + 1}`,
                  }}
                  checked={step.done}
                  onChange={(e) => checklist(index, { done: e.target.checked })}
                  disabled={!canEdit || busy}
                />
                <TextField
                  size="small"
                  fullWidth
                  required
                  label={`Step ${index + 1}`}
                  value={step.text}
                  onChange={(e) => checklist(index, { text: e.target.value })}
                  disabled={!canEdit || busy}
                  inputProps={{ maxLength: 240 }}
                />
                {canEdit && (
                  <IconButton
                    aria-label={`Remove step ${index + 1}`}
                    disabled={busy}
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        checklist: f.checklist.filter((_, i) => i !== index),
                      }))
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}
            {canEdit && (
              <Button
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    checklist: [...f.checklist, { text: "", done: false }],
                  }))
                }
                disabled={busy || form.checklist.length >= 100}
                sx={{ alignSelf: "flex-start" }}
              >
                + Add checklist step
              </Button>
            )}
            {item.id && (
              <>
                <Divider />
                <Typography fontWeight={600}>Completion history</Typography>
                {history === null ? (
                  <Typography variant="body2">Loading history…</Typography>
                ) : history === "error" ? (
                  <Typography color="error">Could not load history.</Typography>
                ) : history.length ? (
                  history.map((entry) => (
                    <Box key={entry.id}>
                      <Typography variant="body2">
                        {entry.completed_name} ·{" "}
                        {new Date(entry.completed_at).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {entry.due_date
                          ? `Due ${formatDate(entry.due_date)}`
                          : "Completed"}
                        {entry.checklist.length
                          ? ` · ${entry.checklist.filter((s) => s.done).length}/${entry.checklist.length} steps checked`
                          : ""}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No completions yet.
                  </Typography>
                )}
              </>
            )}
            {confirmDelete && (
              <Alert
                severity="warning"
                action={
                  <Button color="error" onClick={remove} disabled={busy}>
                    Confirm delete
                  </Button>
                }
              >
                Delete this task, its repeats, and completion history?
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {item.can_delete && (
            <Button
              color="error"
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
              sx={{ mr: "auto" }}
            >
              Delete
            </Button>
          )}
          <Button onClick={onClose} disabled={busy}>
            {canEdit ? "Cancel" : "Close"}
          </Button>
          {canEdit && (
            <Button variant="contained" type="submit" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export function EventEditor({ item, people, onClose, onSaved, onDeleted }) {
  const today = dateKey(new Date());
  const [form, setForm] = useState({
    title: "",
    description: "",
    kind: "event",
    start_date: today,
    end_date: today,
    start_time: "",
    end_time: "",
    person: "",
    recurrence: "none",
    ...item,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canEdit = !item.id || item.can_edit;
  const set = (key) => (event) =>
    setForm((f) => ({ ...f, [key]: event.target.value }));
  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const payload = {
      title: form.title.trim(),
      description: form.description,
      kind: form.kind,
      start_date: form.start_date,
      end_date: form.end_date,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      person: form.person ? Number(form.person) : null,
      recurrence: form.recurrence,
    };
    try {
      const { data } = await (item.id
        ? api.updateEvent(item.id, payload)
        : api.createEvent(payload));
      onSaved(data);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    setBusy(true);
    setError("");
    try {
      await api.deleteEvent(item.id);
      onDeleted();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="event-editor-title"
    >
      <Box component="form" onSubmit={save}>
        <DialogTitle id="event-editor-title">
          {item.id ? "Calendar event" : "Add calendar event"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {!canEdit && (
              <Alert severity="info">
                Only the creator can edit this event.
              </Alert>
            )}
            {item.id && form.recurrence !== "none" && (
              <Alert severity="info">
                Changes apply to the whole repeating event.
              </Alert>
            )}
            <TextField
              autoFocus
              required
              label="Title"
              value={form.title}
              onChange={set("title")}
              disabled={!canEdit || busy}
              inputProps={{ maxLength: 240 }}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Select
                label="Type"
                value={form.kind}
                onChange={set("kind")}
                disabled={!canEdit || busy}
              >
                <option value="event">Event</option>
                <option value="season">Seasonal reminder</option>
                <option value="shift">Weekend shift</option>
              </Select>
              <Select
                label="Person"
                value={form.person || ""}
                onChange={set("person")}
                disabled={!canEdit || busy}
              >
                <option value="">Everyone / no person</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <DateInput
                label="Start date"
                required
                value={form.start_date}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    start_date: e.target.value,
                    end_date:
                      f.end_date < e.target.value ? e.target.value : f.end_date,
                  }))
                }
                disabled={!canEdit || busy}
              />
              <DateInput
                label="End date"
                required
                value={form.end_date}
                onChange={set("end_date")}
                inputProps={{ min: form.start_date }}
                disabled={!canEdit || busy}
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                type="time"
                label="Start time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.start_time || ""}
                onChange={set("start_time")}
                disabled={!canEdit || busy}
              />
              <TextField
                type="time"
                label="End time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.end_time || ""}
                onChange={set("end_time")}
                disabled={!canEdit || busy}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Leave times empty for an all-day event. Shift times are shown as
              entered.
            </Typography>
            <Repeat
              value={form.recurrence}
              onChange={set("recurrence")}
              disabled={!canEdit || busy}
            />
            <TextField
              label="Notes"
              multiline
              minRows={2}
              value={form.description}
              onChange={set("description")}
              disabled={!canEdit || busy}
            />
            <Typography variant="caption" color="text.secondary">
              Visible to the team. For an alert and a completion checkbox, add a
              task with a reminder time.
            </Typography>
            {confirmDelete && (
              <Alert
                severity="warning"
                action={
                  <Button color="error" onClick={remove} disabled={busy}>
                    Confirm delete
                  </Button>
                }
              >
                Delete this event and all its repeats?
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {item.id && canEdit && (
            <Button
              sx={{ mr: "auto" }}
              color="error"
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </Button>
          )}
          <Button onClick={onClose} disabled={busy}>
            {canEdit ? "Cancel" : "Close"}
          </Button>
          {canEdit && (
            <Button variant="contained" type="submit" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
}
